import { FilterChip } from "@components/common/faro-table/faro-table-filter/filter-chip";
import { FilterChipDropdownBackHeader } from "@components/common/faro-table/faro-table-filter/filter-chip-dropdown-back-header";
import { FilterChipDropdownList } from "@components/common/faro-table/faro-table-filter/filter-chip-dropdown-list";
import { NumberRangeFields } from "@components/common/faro-table/faro-table-filter/number-range-filter/number-range-fields";
import { getNumberRangeFilterId } from "@components/common/faro-table/faro-table-filter/number-range-filter/number-range-filter-utils";
import { getTeamsMemberFilterItems } from "@components/teams/teams-filters/members-filter/teams-member-filter-items";
import { TeamsMembersFilterModeList } from "@components/teams/teams-filters/members-filter/teams-members-filter-mode-list";
import { useTeamsOfFilterMembers } from "@components/teams/teams-filters/members-filter/use-teams-of-filter-members";
import { getTeamsMembersFilterState } from "@components/teams/teams-filters/teams-filter-options";
import { TEAMS_MEMBER_COUNT_FILTER_LABEL, TEAMS_MEMBERS_FILTER_COLUMN, TEAMS_MEMBERS_FILTER_LABEL, TEAMS_SPECIFIC_MEMBERS_FILTER_LABEL } from "@components/teams/teams-filters/teams-filter-constants";
import { useCompanyId } from "@context-providers/company-id-provider";
import { Menu, MenuItem } from "@flat-ui";
import { useFilterQueryParams } from "@hooks/table-filters/use-filter-query-params";
import { Box, Stack } from "@mui-wrapper";
import { areCompanyMembersLoadingSelector, companyMembersSelector } from "@store/members/members-selector";
import { fetchCompanyMembers } from "@store/members/members-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { TableType } from "@utils/track-event/track-event-list";
import { useState } from "react";

/** Width of the dropdown, which fits the names of the members. Set on the menu and its paper alike */
const DROPDOWN_WIDTH = "350px";

/**
 * A chip to filter the teams by their members. Its dropdown offers to filter either by a range of
 * the number of members, or by specific members of the workspace, which the user can search and
 * pick several of. A team then passes if it contains any of them. Only one of the two is applied
 * at a time, so applying one removes the other. The members are fetched when the chip is opened,
 * unless they already are.
 */
export function TeamsMembersFilterChip() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [shownMode, setShownMode] = useState();
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  const members = useAppSelector(companyMembersSelector);
  const areMembersLoading = useAppSelector(areCompanyMembersLoadingSelector);
  const {
    getFilterIdsForColumn,
    updateFilterItem
  } = useFilterQueryParams();
  const {
    activeRangeFilterId,
    activeRange,
    activeMemberFilterIds
  } = getTeamsMembersFilterState(getFilterIdsForColumn(TEAMS_MEMBERS_FILTER_COLUMN));
  const {
    hasFailed,
    fetchTeamsOfMembers
  } = useTeamsOfFilterMembers(activeMemberFilterIds);

  /** Applies the filter, replacing the other mode if it was applied, or removes it if it was applied */
  function toggleFilter(filterId) {
    updateFilterItem({
      tableType: TableType.teams,
      filteredColumn: TEAMS_MEMBERS_FILTER_COLUMN,
      filterId,
      shouldReplaceFilterInColumn: activeRange !== undefined,
      trackingProps: {
        filterMode: "members"
      }
    });
  }

  /** Applies the range, or removes the applied one if the user emptied both limits */
  function applyRange(range) {
    const filterId_0 = range ? getNumberRangeFilterId(range) : activeRangeFilterId;
    if (filterId_0 !== undefined && (!range || filterId_0 !== activeRangeFilterId)) {
      updateFilterItem({
        tableType: TableType.teams,
        filteredColumn: TEAMS_MEMBERS_FILTER_COLUMN,
        filterId: filterId_0,
        shouldReplaceFilterInColumn: true,
        trackingProps: {
          filterMode: "count"
        }
      });
    }
    closeDropdown();
  }
  function openDropdown(element) {
    setAnchorEl(element);
    // The applied mode is shown right away, so the user sees what they picked
    setShownMode(getAppliedMode(activeRange, activeMemberFilterIds));
    void dispatch(fetchCompanyMembers({
      companyId
    }));
    // Fetches again the teams of the members that failed to be fetched
    fetchTeamsOfMembers();
  }
  function closeDropdown() {
    setAnchorEl(null);
    setShownMode(undefined);
  }
  return <Box component="div">
      <FilterChip filteredColumn={TEAMS_MEMBERS_FILTER_COLUMN} label={TEAMS_MEMBERS_FILTER_LABEL} shouldHaveDropdown={true} isDropdownOpen={!!anchorEl} onClick={event => openDropdown(event.currentTarget)} />

      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={closeDropdown} minWidth={DROPDOWN_WIDTH} slotProps={{
      paper: {
        sx: {
          width: DROPDOWN_WIDTH,
          maxHeight: "330px",
          overflowY: "auto"
        }
      }
    }}>
        {shownMode === undefined && <TeamsMembersFilterModeList appliedMode={getAppliedMode(activeRange, activeMemberFilterIds)} onModeClicked={setShownMode} />}

        {shownMode !== undefined && <Stack sx={{
        px: 1,
        pt: 1
      }}>
            <FilterChipDropdownBackHeader title={shownMode === "count" ? TEAMS_MEMBER_COUNT_FILTER_LABEL : TEAMS_SPECIFIC_MEMBERS_FILTER_LABEL} backLabel="Back to the member filters" onBack={() => setShownMode(undefined)} />
          </Stack>}

        {shownMode === "count" && <NumberRangeFields countedName="members" activeRange={activeRange} onCancel={closeDropdown} onApply={applyRange} />}

        {shownMode === "members" && hasFailed && <MenuItem label="Failed to load the teams of some members. Select to try again." onClick={fetchTeamsOfMembers} sx={{
        textWrap: "wrap",
        color: "error.main"
      }} />}

        {shownMode === "members" && <FilterChipDropdownList label={TEAMS_MEMBERS_FILTER_LABEL} items={getTeamsMemberFilterItems(members, activeMemberFilterIds)} activeFilterIds={activeMemberFilterIds} shouldShowSearchBox={true} shouldShowCheckbox={true} isLoading={areMembersLoading} onItemClicked={toggleFilter} onEscape={closeDropdown} />}
      </Menu>
    </Box>;
}

/** Returns the mode of the members filter the user applied, if any */
function getAppliedMode(activeRange, activeMemberFilterIds) {
  if (activeRange) {
    return "count";
  }
  return activeMemberFilterIds.length > 0 ? "members" : undefined;
}